import { Button, DatePicker, Form, Input, InputNumber, Select, Typography } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addTravel, loadAllTravel, updateTravel } from "../../redux/rtk/features/travel/travelSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import getUserFromToken from "../../utils/getUserFromToken";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AddTravelRequest = ({ form, isEditMode, setIsEditMode, currentTravelId, setCurrentTravelId }) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = useState(false);
	const staffList = useSelector((state) => state.users?.list || []);
	const employeeId = getUserFromToken();

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const travelData = {
				...values,
				employeeId: values.employeeId || employeeId,
				travelDate: dayjs(values.travelDate).format("YYYY-MM-DD"),
				returnDate: values.returnDate ? dayjs(values.returnDate).format("YYYY-MM-DD") : null,
				DataStatus: 1,
			};

			if (isEditMode && currentTravelId) {
				const resp = await dispatch(updateTravel({ id: currentTravelId, values: travelData }));
				if (resp.payload?.message === "success") {
					form.resetFields();
					setIsEditMode(false);
					setCurrentTravelId(null);
					dispatch(loadAllTravel());
				}
			} else {
				const resp = await dispatch(addTravel(travelData));
				if (resp.payload?.message === "success") {
					form.resetFields();
					dispatch(loadAllTravel());
				}
			}
		} catch (error) {
			toast.error("Failed to submit travel request");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	const resetForm = () => {
		form.resetFields();
		setIsEditMode(false);
		setCurrentTravelId(null);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				{isEditMode ? "Edit Travel Request" : "Add Travel Request"}
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="travel-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				<Form.Item
					label={<span className="required-label">Employee</span>}
					name="employeeId"
					rules={[
						{ required: true, message: "Please select an employee!" },
					]}
				>
					<Select
						placeholder="Select employee"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">From (Origin)</span>}
					name="fromLocation"
					rules={[
						{ required: true, message: "Please enter origin location!" },
					]}
				>
					<Input placeholder="Enter origin location" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">To (Destination)</span>}
					name="toLocation"
					rules={[
						{ required: true, message: "Please enter destination location!" },
					]}
				>
					<Input placeholder="Enter destination location" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Travel Date</span>}
					name="travelDate"
					rules={[
						{ required: true, message: "Please select travel date!" },
					]}
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
						disabledDate={(current) => current && current < dayjs().startOf("day")}
					/>
				</Form.Item>

				<Form.Item
					label="Return Date"
					name="returnDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
						disabledDate={(current) => {
							const travelDate = form.getFieldValue("travelDate");
							if (!travelDate) return current && current < dayjs().startOf("day");
							return current && current < dayjs(travelDate).startOf("day");
						}}
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Purpose</span>}
					name="purpose"
					rules={[
						{ required: true, message: "Please enter travel purpose!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="Enter travel purpose"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Estimated Cost (₹)"
					name="estimatedCost"
				>
					<InputNumber
						style={{ width: "100%" }}
						placeholder="Enter estimated cost"
						min={0}
						step={100}
						addonBefore="₹"
						formatter={(value) =>
							`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
						}
						parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
					/>
				</Form.Item>

				<Form.Item
					label="Travel Mode"
					name="travelMode"
				>
					<Select placeholder="Select travel mode">
						<Option value="Flight">Flight</Option>
						<Option value="Train">Train</Option>
						<Option value="Bus">Bus</Option>
						<Option value="Car">Car</Option>
						<Option value="Other">Other</Option>
					</Select>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						size="large"
						htmlType="submit"
						block
						loading={loader}
					>
						{isEditMode ? "Update Travel Request" : "Submit Travel Request"}
					</Button>
					{isEditMode && (
						<Button
							size="large"
							onClick={resetForm}
							block
							style={{ marginTop: "10px" }}
						>
							Cancel
						</Button>
					)}
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddTravelRequest;

